import { useNavigate } from 'react-router';

import { Button } from '@llura/ui';

import { signIn } from '../../lib/auth-client';
import { AUTH_REQUIRED, GOOGLE_OAUTH_ENABLED } from '../../lib/config';
import { useStrings } from '../../hooks/useStrings';

/**
 * Post-coach signup gate. Offers account creation (Google or email) to unlock
 * cross-device sync + reminders, but is skippable — the app works fully on this
 * device without an account. When AUTH_REQUIRED flips on, the skip button is
 * hidden.
 */
export function SignupGatePage() {
  const navigate = useNavigate();
  const { strings } = useStrings();
  const t = strings.auth.gate;
  const oauth = strings.auth.oauth;

  // OAuth uses a full-page redirect and returns to '/', where RootRedirect +
  // boot-sync claim the local draft and route onward. localStorage survives
  // the round-trip, so the snapshot is intact when control returns.
  const continueWithGoogle = () => {
    void signIn.social({ provider: 'google', callbackURL: `${window.location.origin}/` });
  };

  return (
    <div className="relative min-h-[100svh] overflow-hidden bg-background">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 right-[-15%] h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-10%] left-[-10%] h-[380px] w-[380px] rounded-full bg-secondary/10 blur-3xl"
      />

      <header className="sticky top-0 z-10 flex items-center gap-2 bg-background/80 px-4 py-3 backdrop-blur">
        <span className="font-serif text-lg">{strings.brand.name}</span>
      </header>

      <main className="relative mx-auto w-full max-w-md px-5 pb-16 pt-12">
        <h1 className="font-serif text-3xl">{t.title}</h1>
        <p className="mt-3 text-sm text-muted-foreground">{t.intro}</p>

        <div className="mt-8 space-y-3">
          {GOOGLE_OAUTH_ENABLED && (
            <>
              <Button variant="secondary" className="w-full" onClick={continueWithGoogle}>
                {oauth.googleCta}
              </Button>

              <div className="flex items-center gap-3 py-1 text-xs text-muted-foreground">
                <span className="h-px flex-1 bg-border" />
                {oauth.orSeparator}
                <span className="h-px flex-1 bg-border" />
              </div>
            </>
          )}

          <Button className="w-full" onClick={() => navigate('/signup')}>
            {t.createCta}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => navigate('/login')}>
            {t.loginCta}
          </Button>

          {!AUTH_REQUIRED && (
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => navigate('/onboarding/chat')}
            >
              {t.skipCta}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
